import { Box, Chip, Stack, Typography, useTheme } from '@mui/material';
import { memo, useMemo } from 'react';
import {
	formatWebhookMonitoringDateTime,
	getWebhookMonitoringStatusChipSx,
	resolveGeneralTranslationLabel,
} from 'utils/webhook-monitoring.utils';
import {
	DetailsLabelValue,
	getDetailsModalFieldLabelSx,
	getDetailsModalFieldValueSx,
} from 'components/DetailsModal/DetailsLabelValue';

type Translate = (key: string) => string;

type Props = {
	operation: any;
	summaryRow: any;
	attemptsTotal: number;
	t: Translate;
	summaryPanelSx: object;
	/** Shown in this card as plain label/value rows (same as other fields), not a separate alert. */
	showLastError?: boolean;
};

export const DetailsOverview = memo(function DetailsOverview({
	operation,
	summaryRow,
	attemptsTotal,
	t,
	summaryPanelSx,
	showLastError = false,
}: Props) {
	const theme = useTheme();
	const fieldLabelSx = useMemo(() => getDetailsModalFieldLabelSx(theme), [theme]);
	const fieldValueSx = useMemo(() => getDetailsModalFieldValueSx(theme), [theme]);

	const overviewGridSx = useMemo(
		() => ({ ...summaryPanelSx, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }),
		[summaryPanelSx]
	);

	if (!operation) {
		return null;
	}

	const businessStatusLabel = resolveGeneralTranslationLabel(summaryRow?.businessStatus);
	const operationTypeLabel = resolveGeneralTranslationLabel(summaryRow?.operationType);

	return (
		<Box sx={{ WebkitFontSmoothing: 'antialiased' }}>
			<Box sx={overviewGridSx}>
				<Stack spacing={0.5}>
					<Typography component="span" sx={fieldLabelSx}>
						{t('page.webhookMonitoring.field.provider')}
					</Typography>
					<Typography component="span" sx={fieldValueSx}>
						{t(`page.webhookMonitoring.provider.${operation.provider}`)}
					</Typography>
				</Stack>
				<Stack spacing={0.5}>
					<Typography component="span" sx={fieldLabelSx}>
						{t('page.webhookMonitoring.field.operationType')}
					</Typography>
					<Typography component="span" sx={fieldValueSx}>
						{operationTypeLabel ?? '—'}
					</Typography>
				</Stack>
				<Stack spacing={0.5}>
					<Typography component="span" sx={fieldLabelSx}>
						{t('page.webhookMonitoring.field.webhookStatus')}
					</Typography>
					<Box>
						<Chip
							label={t(`page.webhookMonitoring.status.${operation.status}`)}
							size="small"
							sx={[
								getWebhookMonitoringStatusChipSx(operation.status, theme),
								{
									fontWeight: theme.typography.fontWeightRegular,
									'& .MuiChip-label': {
										...theme.typography.subtitle,
										fontWeight: theme.typography.fontWeightRegular,
										lineHeight: 1.4,
										color: 'inherit',
									},
								},
							]}
						/>
					</Box>
				</Stack>
				<Stack spacing={0.5}>
					<Typography component="span" sx={fieldLabelSx}>
						{t('page.webhookMonitoring.field.businessStatus')}
					</Typography>
					<Typography component="span" sx={fieldValueSx}>
						{businessStatusLabel ?? '—'}
					</Typography>
				</Stack>
				<DetailsLabelValue
					label={t('page.webhookMonitoring.field.createdAt')}
					value={formatWebhookMonitoringDateTime(operation.createdAt)}
				/>
				<DetailsLabelValue
					label={t('page.webhookMonitoring.field.updatedAt')}
					value={formatWebhookMonitoringDateTime(operation.updatedAt)}
				/>
				<DetailsLabelValue
					label={t('page.webhookMonitoring.field.lastAttemptedAt')}
					value={operation.lastAttemptedAt ? formatWebhookMonitoringDateTime(operation.lastAttemptedAt) : ''}
				/>
				{operation.lastAttemptOutcome ? (
					<DetailsLabelValue
						label={t('page.webhookMonitoring.field.lastAttemptOutcome')}
						value={t(`page.webhookMonitoring.attempt.outcome.${operation.lastAttemptOutcome}`)}
					/>
				) : null}
				{showLastError && operation?.lastError ? (
					<DetailsLabelValue
						label={t('page.webhookMonitoring.field.lastError')}
						value={operation.lastError}
						breakAll
					/>
				) : null}
				{showLastError && operation?.failurePhase ? (
					<DetailsLabelValue
						label={t('page.webhookMonitoring.field.failurePhase')}
						value={operation.failurePhase}
						breakAll
					/>
				) : null}
				<DetailsLabelValue
					label={t('page.webhookMonitoring.field.nextRetryAt')}
					value={operation.nextRetryAt ? formatWebhookMonitoringDateTime(operation.nextRetryAt) : ''}
				/>
				{operation.replayReason ? (
					<DetailsLabelValue
						label={t('page.webhookMonitoring.field.replayReason')}
						value={t(`page.webhookMonitoring.replayReason.${operation.replayReason}`)}
					/>
				) : null}
				<DetailsLabelValue label={t('page.webhookMonitoring.field.totalAttempts')} value={attemptsTotal} />
				<DetailsLabelValue label={t('page.webhookMonitoring.field.retries')} value={operation?.retries} />
				<DetailsLabelValue
					label={t('page.webhookMonitoring.field.replayCount')}
					value={operation?.replayCount}
				/>
				{operation.lastReplayRequestedAt ? (
					<DetailsLabelValue
						label={t('page.webhookMonitoring.field.lastReplayRequestedAt')}
						value={formatWebhookMonitoringDateTime(operation.lastReplayRequestedAt)}
					/>
				) : null}
				{operation.lastReplayRequestedByBoUserName ? (
					<DetailsLabelValue
						label={t('page.webhookMonitoring.field.lastReplayRequestedBy')}
						value={operation.lastReplayRequestedByBoUserName}
					/>
				) : null}
			</Box>
		</Box>
	);
});
